import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const NotificationContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`.replace(/^http/, 'ws');

// Generate a subtle notification tone using Web Audio API
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1046, ctx.currentTime + 0.08);
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.16);
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
    setTimeout(() => ctx.close(), 500);
  } catch {}
}

function showBrowserNotification(title, body) {
  if (Notification.permission === 'granted' && document.hidden) {
    try {
      new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: 'grassroots-chat',
        renotify: true,
      });
    } catch {}
  }
}

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [permissionState, setPermissionState] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const token = localStorage.getItem('token');

  const addNotification = useCallback((notif) => {
    const entry = {
      id: Date.now() + Math.random(),
      ...notif,
      timestamp: new Date().toISOString(),
      read: false,
    };
    setNotifications(prev => [entry, ...prev].slice(0, 50));
    setUnreadCount(prev => prev + 1);
    playNotificationSound();
    showBrowserNotification(notif.title, notif.body);
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      const result = await Notification.requestPermission();
      setPermissionState(result);
    }
  }, []);

  // WebSocket connection for real-time events
  const connectWs = useCallback(() => {
    if (!token) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(`${WS_BASE}/chat/ws/admin?token=${token}`);
    wsRef.current = ws;

    ws.onmessage = (evt) => {
      try {
        const event = JSON.parse(evt.data);
        if (event.type === 'new_message' && event.message?.role === 'user') {
          const name = event.message.sender_name || 'Visitor';
          const content = event.message.content || '';
          addNotification({
            title: `New message from ${name}`,
            body: content.length > 80 ? content.slice(0, 80) + '...' : content,
            type: 'message',
            sessionId: event.session_id,
          });
        }
        if (event.type === 'session_update' && event.status === 'escalated') {
          addNotification({
            title: 'Chat Escalated',
            body: `Session ${event.session_id} has been escalated to a human agent`,
            type: 'escalation',
            sessionId: event.session_id,
          });
        }
      } catch {}
    };

    ws.onclose = () => {
      wsRef.current = null;
      reconnectTimer.current = setTimeout(connectWs, 3000);
    };

    ws.onerror = () => ws.close();

    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      else clearInterval(ping);
    }, 25000);
    ws._pingInterval = ping;
  }, [token, addNotification]);

  useEffect(() => {
    connectWs();
    requestPermission();
    return () => {
      if (wsRef.current) {
        clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connectWs, requestPermission]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        permissionState,
        markAllRead,
        clearNotifications,
        requestPermission,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
